/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useEffect  } from 'react';
import './COAUForm.css';
import { Capacitor } from '@capacitor/core';
import { useAppStore } from '../../store/useAppStore';
import { CollapsibleSection } from './CollapsibleSection';
import { MicInput, MicTextarea, MicTimeInput } from '../common/MicInput';
import ProvinciaInput from '../common/ProvinciaInput';
import ModalInvioEmail from './ModalInvioEmail';
import { generaReportCOAU } from '../../utils/coauReport';

interface RichiestaPerOption {
  value: string;
  label: string;
}

interface ValoreAmbientaleOption {
  value: number;
  label: string;
  color: string;
}

interface TipologiaOption {
  value: string;
  label: string;
}

interface DirezioneSvento {
  gradi: string;
  direzione: string;
  nome: string;
  provenienza: string;
  caratteristiche: string;
}

interface COAUFormProps {
  onOpenMap?: () => void;
}

export const COAUForm: React.FC<COAUFormProps> = ({ onOpenMap }) => {
  const { coau, updateCOAU, setCOAU } = useAppStore();

  // Stati per modali
  const [showRichiestaModal, setShowRichiestaModal] = useState(false);
  const [showValoreBruciatoModal, setShowValoreBruciatoModal] = useState(false);
  const [showTipologiaBruciatoModal, setShowTipologiaBruciatoModal] = useState(false);
  const [showValoreRischioModal, setShowValoreRischioModal] = useState(false);
  const [showTipologiaRischioModal, setShowTipologiaRischioModal] = useState(false);
  const [showModalEmail, setShowModalEmail] = useState(false);
  const [nomeAllegatoEmail, setNomeAllegatoEmail] = useState<string>('');
  const [showVentoModal, setShowVentoModal] = useState(false);
  const [showGPSModal, setShowGPSModal] = useState(false);
  const [showTensioneModal, setShowTensioneModal] = useState(false);
  const [showStatoElettrodottoModal, setShowStatoElettrodottoModal] = useState(false);
  const [showRegioneAereiModal, setShowRegioneAereiModal] = useState(false);
  const [showBacinoAereiModal, setShowBacinoAereiModal] = useState(false);
  const [showMappaBacinoModal, setShowMappaBacinoModal] = useState(false);
  const [showRadioFrequenzaModal, setShowRadioFrequenzaModal] = useState(false);
  const [showBussolaModal, setShowBussolaModal] = useState(false);
  const [oraRichiestaError, setOraRichiestaError] = useState(false);

  // Stato per sezioni collapsabili
  const [openSections, setOpenSections] = useState({
    orario: true,
    localizzazione: false,
    bruciato: false,
    rischio: false,
    fronteFuoco: false,
    fronteUnico: false,
    fronteDiversi: false,
    vento: false,
    orografia: false,
    quota: false,
    criticita: false,
    infrastrutture: false,
    insediamenti: false,
    ostacoliAereiDett: false,
    ostacoliTerraDett: false,
    ostacoliAerei: false,
    ostacoliTerra: false,
    fontiIdriche: false,
    fontiAereiDett: false,
    fontiElicotteriDett: false,
    fontiAerei: false,
    fontiElicotteri: false,
    personale: false,
    coordinatore: false,
    radioTBT: false,
    squadre: false,
    aeromobili: false,
    ritardante: false,
    firma: false
  });

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Stato per ostacolo corrente VISIBILE (solo uno alla volta)
  const [ostacoloVisibileCorrente, setOstacoloVisibileCorrente] = useState<string | null>(null);
  const [ostacoloTerraVisibileCorrente, setOstacoloTerraVisibileCorrente] = useState<string | null>(null);

  // Stati form - Inizializza con dati esistenti da COAU
  const [formData, setFormData] = useState({
    oraAccertamento: (coau as any)?.oraAccertamento || '',
    oraRichiesta: (coau as any)?.oraRichiesta || '',
    richiestaPer: (coau as any)?.richiestaPer || '',
    comune: (coau as any)?.comune || '',
    provincia: (coau as any)?.provincia || '',
    indirizzo: (coau as any)?.indirizzo || '',
    latitudine: (coau as any)?.latitudine || '',
    longitudine: (coau as any)?.longitudine || '',
    valoreBruciato: (coau as any)?.valoreBruciato || null as number | null,
    tipologiaBruciato: (coau as any)?.tipologiaBruciato || '',
    ettariBruciato: (coau as any)?.ettariBruciato || '',
    valoreRischio: (coau as any)?.valoreRischio || null as number | null,
    tipologiaRischio: (coau as any)?.tipologiaRischio || '',
    ettariRischio: (coau as any)?.ettariRischio || '',
    fronteUnico: (coau as any)?.fronteUnico || '',
    fronteDiversiN: (coau as any)?.fronteDiversiN || '',
    fronteDiversiMt: (coau as any)?.fronteDiversiMt || '',
    intensitaVento: (coau as any)?.intensitaVento || '',
    direzioneVento: (coau as any)?.direzioneVento || null as DirezioneSvento | null,
    velocitaVentoKmh: (coau as any)?.velocitaVentoKmh || null as number | null,
    quota: (coau as any)?.quota || '',
    tipoTerreno: (coau as any)?.tipoTerreno || '',
    hasInfrastrutture: (coau as any)?.hasInfrastrutture || 'NO',
    noteInfrastrutture: (coau as any)?.noteInfrastrutture || '',
    hasInsediamenti: (coau as any)?.hasInsediamenti || 'NO',
    noteInsediamenti: (coau as any)?.noteInsediamenti || '',
    // OSTACOLI AEREI
    ostacoliCompilati: (coau as any)?.ostacoliCompilati || [] as string[],
    elettrodotto: (coau as any)?.elettrodotto || {
      stato: 'Attivo',
      tensione: '',
      direzione: 'Direzione'
    },
    ostacoliDirezioni: (coau as any)?.ostacoliDirezioni || {} as Record<string, string>,
    visibilitaNote: (coau as any)?.visibilitaNote || '',
    // OSTACOLI A TERRA
    ostacoliTerraCompilati: (coau as any)?.ostacoliTerraCompilati || [] as string[],
    ostacoliTerraDirezioni: (coau as any)?.ostacoliTerraDirezioni || {} as Record<string, string>,
    // FONTE IDRICA
    fonteIdricaAerei: (coau as any)?.fonteIdricaAerei || {
      regione: '',
      bacino: '',
      bacinoData: null as { name: string; cat: string; coords: string } | null
    },
    fonteIdricaElicotteri: (coau as any)?.fonteIdricaElicotteri || {
      tipo: '',
      dettagli: ''
    },
    // PERSONALE E MEZZI
    coordinatore: (coau as any)?.coordinatore || 'NO',
    numeroPersone: (coau as any)?.numeroPersone || '',
    nominativoDOS: (coau as any)?.nominativoDOS || '',
    radioFrequenzaTBT: (coau as any)?.radioFrequenzaTBT || '',
    squadre: (coau as any)?.squadre || 'NO',
    aeromobiliReg: (coau as any)?.aeromobiliReg || 'NO',
    nomeAeromobile: (coau as any)?.nomeAeromobile || '',
    radioAeromobile: (coau as any)?.radioAeromobile || '',
    // RITARDANTE
    ritardante: (coau as any)?.ritardante || 'NO',
    noteRitardante: (coau as any)?.noteRitardante || '',
    // FIRMA
    firma: (coau as any)?.firma || ''
  });

  // SALVATAGGIO AUTOMATICO con debounce (1 secondo dopo l'ultima modifica)
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('=== AUTO-SALVATAGGIO DATI COAU ===');
      updateCOAU(formData);
    }, 1000); // Salva dopo 1 secondo di inattività
    
    return () => clearTimeout(timer);
  }, [formData, updateCOAU]);

  // Opzioni
  const richiestePerOptions: RichiestaPerOption[] = [
    { value: 'soppressione', label: 'Soppressione' },
    { value: 'contenimento', label: 'Contenimento' },
    { value: 'bonifica', label: 'Bonifica' },
    { value: 'ricognizione', label: 'Ricognizione/Sorveglianza' },
    { value: 'ricognizione_armata', label: 'Ricognizione armata' }
  ];

  const valoriAmbientali: ValoreAmbientaleOption[] = [
    { value: 1, label: '1 - Basso: nessuna protezione, erbe, sterpaglie, arbusti e macchia bassa', color: '#4CAF50' },
    { value: 2, label: '2 - Medio: Parchi e Riserve Regionali, boschi e macchia alta', color: '#FFEB3B' },
    { value: 3, label: '3 - Alto: Parchi Nazionali, Riserve statali, boschi vetusti e ad alta naturalità', color: '#FF9800' },
    { value: 4, label: '4 - Eccezionale: Riserve integrali, specie protette, aree di monitoraggio', color: '#F44336' }
  ];

  const tipologie: TipologiaOption[] = [
    { value: 'I', label: 'I - Erba e sterpaglia' },
    { value: 'II', label: 'II - Arbusti, bassa macchia e formazioni degradate' },
    { value: 'III', label: 'III - Alta macchia, cedui, fustaia di latifoglie, bosco di conifere < 2m, rimboschimenti' },
    { value: 'IV', label: 'IV - Bosco di conifere di altezza superiore a 2 metri' }
  ];

  const rosaVenti = [
    {
      gradi: '0°',
      direzione: 'Nord',
      nome: 'Tramontana',
      provenienza: 'Tra i monti delle Alpi',
      caratteristiche: 'Vento freddo che porta con sé le basse temperature del continente europeo'
    },
    {
      gradi: '45°',
      direzione: 'Nord-Est',
      nome: 'Grecale',
      provenienza: 'Dalla Grecia',
      caratteristiche: 'Vento freddo e secco in Inverno, fresco e piacevole d\'Estate'
    },
    {
      gradi: '90°',
      direzione: 'Est',
      nome: 'Levante',
      provenienza: 'Da dove sorge (leva) il Sole',
      caratteristiche: 'Vento fresco e umido, caldo in Estate e freddo in Inverno'
    },
    {
      gradi: '135°',
      direzione: 'Sud-Est',
      nome: 'Scirocco',
      provenienza: 'Dalla Siria',
      caratteristiche: 'Vento caldo e umido, portatore di piogge'
    },
    {
      gradi: '180°',
      direzione: 'Sud',
      nome: 'Ostro',
      provenienza: 'Dall\'emisfero australe',
      caratteristiche: 'Vento caldo e umido, detto anche di Mezzogiorno'
    },
    {
      gradi: '225°',
      direzione: 'Sud-Ovest',
      nome: 'Libeccio',
      provenienza: 'Dalla Libia',
      caratteristiche: 'Vento caldo e portatore di sabbia del deserto'
    },
    {
      gradi: '270°',
      direzione: 'Ovest',
      nome: 'Ponente',
      provenienza: 'Da dove tramonta (pone) il Sole',
      caratteristiche: 'Vento fresco e piacevole'
    },
    {
      gradi: '315°',
      direzione: 'Nord-Ovest',
      nome: 'Maestrale',
      provenienza: 'Da Roma, la via maestra',
      caratteristiche: 'Vento fresco e sereno, ma può preannunciare una perturbazione'
    }
  ];

  const importaVentoDaSITAC = () => {
    try {
      const raw = localStorage.getItem('DOS518_SITAC_STATE');
      if (!raw) { alert('Nessun dato vento disponibile dalla SITAC.\nImposta prima il vento nella sezione SITAC.'); return; }
      const sitac = JSON.parse(raw);
      const deg: number = sitac.windDirectionDeg ?? 225;
      const kmh: number = sitac.windSpeedKmh ?? 0;
      const step = Math.round((((deg % 360) + 360) % 360) / 45) % 8;
      const rosa = rosaVenti[step];
      const classe = kmh < 5 ? 'Debole' : kmh < 20 ? 'Moderato' : 'Forte';
      setFormData(prev => ({
        ...prev,
        intensitaVento: classe,
        direzioneVento: rosa,
        velocitaVentoKmh: Math.round(kmh),
      }));
    } catch {
      alert('Errore nella lettura dei dati SITAC');
    }
  };

  const ostacoliOptions = [
    'Elettrodotti', 'Linee Telefoniche', 'Teleferiche', 'Cabinovie',
    'Seggiovie', 'Palorci', 'Ponti Tibetani', 'Visibilità'
  ];

  const ostacoliTerraOptions = [
    'Tralicci elettrodotto',
    'Pali Linea Telefonica',
    'Ciminiere',
    'Silos',
    'Campanili',
    'Tralicci',
    'Trasmettitori onde radio',
    'Pale Eoliche'
  ];

  // MAPPATURA: Ostacolo Aereo → Ostacolo Terra corrispondente
  const mappaOstacoliAereiTerra: Record<string, string> = {
    'Elettrodotti': 'Tralicci elettrodotto',
    'Linee Telefoniche': 'Pali Linea Telefonica',
    'Teleferiche': 'Tralicci',
    'Cabinovie': 'Tralicci',
    'Seggiovie': 'Tralicci',
    'Palorci': 'Tralicci',
    'Ponti Tibetani': 'Tralicci'
  };

  // Funzione per calcolare distanza tra due coordinate (formula Haversine)
  const calcolaDistanza = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Raggio della Terra in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanza = R * c;
    return distanza; // Ritorna distanza in km
  };

  // Funzione per ottenere distanza dall'incendio al bacino
  const getDistanzaBacino = (): string | null => {
    if (!formData.latitudine || !formData.longitudine || !formData.fonteIdricaAerei.bacinoData) {
      return null;
    }

    const [latIncendio, lonIncendio] = [
      parseFloat(formData.latitudine),
      parseFloat(formData.longitudine)
    ];
    const [latBacino, lonBacino] = formData.fonteIdricaAerei.bacinoData.coords
      .split(',')
      .map(parseFloat);

    if (isNaN(latIncendio) || isNaN(lonIncendio) || isNaN(latBacino) || isNaN(lonBacino)) {
      return null;
    }

    const distanzaKm = calcolaDistanza(latIncendio, lonIncendio, latBacino, lonBacino);
    return distanzaKm.toFixed(2) + ' km';
  };
  const getTestoDaOstacoloAereo = (ostacoloTerra: string): string => {
    // Trova quale ostacolo aereo corrisponde a questo ostacolo terra
    const ostacoloAereo = Object.keys(mappaOstacoliAereiTerra).find(
      aereo => mappaOstacoliAereiTerra[aereo] === ostacoloTerra
    );

    if (!ostacoloAereo) {
      // Nessun corrispondente aereo → usa "Direzione alto"
      return 'Direzione alto';
    }

    // Se è Elettrodotti, usa elettrodotto.direzione
    if (ostacoloAereo === 'Elettrodotti') {
      return formData.elettrodotto.direzione + ' alto';
    }

    // Per gli altri, usa ostacoliDirezioni
    const testoAereo = formData.ostacoliDirezioni[ostacoloAereo] || 'Direzione';
    return testoAereo + ' alto';
  };

  const tensioniOptions = [
    {
      value: 'Alta Tensione 100/400 kV',
      descrizione: 'Trasporto su lunghe distanze, alta potenza, collegamento tra centrali e sottostazioni'
    },
    {
      value: 'Media Tensione 1/100 kV',
      descrizione: 'Distribuzione nelle città e zone industriali, collegamento tra stazioni di trasformazione e utenti finali'
    },
    {
      value: 'Bassa Tensione <1 kV',
      descrizione: 'Utilizzo finale, distribuzione nelle abitazioni e piccole attività'
    }
  ];

  // BACINI AEREI PER REGIONE
  const baciniAereiJSON: Record<string, Array<{ name: string; cat: string; coords: string }>> = {
    "ABRUZZO": [
      { name: "Barrea", cat: "B", coords: "41.745,13.995" },
      { name: "Bomba", cat: "A", coords: "42.100,13.600" }
    ],
    "BASILICATA": [
      { name: "Camastra", cat: "B", coords: "40.500,15.800" },
      { name: "Pietra del Pertusillo", cat: "B", coords: "40.300,16.000" }
    ],
    "CALABRIA": [
      { name: "Ampollino", cat: "A", coords: "39.350,16.450" },
      { name: "Arvo", cat: "A", coords: "39.150,16.600" }
    ],
    "CAMPANIA": [
      { name: "Campolattaro", cat: "B", coords: "41.200,14.700" }
    ],
    "FRIULI V. G.": [
      { name: "Cavazzo", cat: "B", coords: "46.350,13.000" }
    ],
    "LAZIO": [
      { name: "Albano", cat: "B", coords: "41.750,12.650" },
      { name: "Bolsena", cat: "A", coords: "42.600,11.950" },
      { name: "Bracciano", cat: "A", coords: "42.100,12.200" }
    ],
    "LOMBARDIA": [
      { name: "Annone", cat: "B", coords: "45.900,9.200" },
      { name: "Cancano", cat: "B", coords: "46.400,10.400" },
      { name: "Como", cat: "A", coords: "45.800,9.100" }
    ],
    "MARCHE": [
      { name: "Castreccioni", cat: "B", coords: "43.400,12.900" }
    ],
    "MOLISE": [
      { name: "Guardialfiera", cat: "A", coords: "41.700,14.600" }
    ],
    "PIEMONTE": [
      { name: "D'Orta", cat: "A", coords: "45.800,8.400" },
      { name: "Maggiore", cat: "A", coords: "45.900,8.600" }
    ],
    "PUGLIA": [
      { name: "Locone", cat: "A", coords: "40.800,16.200" },
      { name: "Occhito", cat: "A", coords: "41.500,15.000" },
      { name: "Varano", cat: "B", coords: "41.900,15.800" }
    ],
    "SARDEGNA": [
      { name: "Coghinas", cat: "A", coords: "40.900,8.900" },
      { name: "Omodeo", cat: "A", coords: "39.900,8.900" }
    ],
    "SICILIA": [
      { name: "Ancipa", cat: "B", coords: "37.800,14.600" },
      { name: "Poma", cat: "A", coords: "37.600,14.400" }
    ],
    "TOSCANA": [
      { name: "Bilancino", cat: "B", coords: "44.000,11.300" }
    ],
    "VALLE D'AOSTA": [
      { name: "Place Moulin", cat: "B", coords: "45.800,7.300" }
    ],
    "VENETO": [
      { name: "Garda", cat: "A", coords: "45.600,10.700" },
      { name: "Santa Croce", cat: "B", coords: "46.100,12.300" }
    ]
  };

  const regioniItalia = Object.keys(baciniAereiJSON).sort();

  const frequenzeRadioTBT = [
    { freq: '122.150 MHz', zona: 'Intero Territorio Nazionale' },
    { freq: '122.350 MHz', zona: 'Intero Territorio Nazionale' },
    { freq: '134.550 MHz', zona: 'Lombardia ed Emilia Romagna' },
    { freq: '128.500 MHz', zona: 'Valle d\'Aosta, Piemonte, Liguria e Sicilia' },
    { freq: '118.775 MHz', zona: 'Toscana e Umbria' },
    { freq: '118.525 MHz', zona: 'Sardegna, Calabria, Puglia e Basilicata' },
    { freq: '119.025 MHz', zona: 'Lazio' },
    { freq: '118.625 MHz', zona: 'Campania' },
    { freq: '118.925 MHz', zona: 'Abruzzo, Molise, Marche' },
    { freq: '141.100 MHz', zona: 'Intero Territorio Nazionale (Militare, Secondaria)' },
    { freq: '142.500 MHz', zona: 'Intero Territorio Nazionale (Militare, Secondaria)' },
    { freq: '156.800 MHz FM', zona: 'Primo Contatto CP ("Canale 16" Nautico)' }
  ];

  // Handlers
  const handleInputChange = (field: string, value: any) => {
    // Validazione orari: ora richiesta non può precedere accertamento
    if (field === 'oraRichiesta') {
      const accert = formData.oraAccertamento;
      if (accert && value && value < accert) {
        setOraRichiestaError(true);
        return; // blocca il salvataggio
      }
      setOraRichiestaError(false);
    }
    if (field === 'oraAccertamento') {
      // Se l'accertamento viene spostato dopo la richiesta, cancella la richiesta
      if (formData.oraRichiesta && value && value > formData.oraRichiesta) {
        setFormData(prev => ({ ...prev, [field]: value, oraRichiesta: '' }));
        setOraRichiestaError(false);
        return;
      }
      setOraRichiestaError(false);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Calcola automaticamente tipo terreno in base alla quota
    if (field === 'quota') {
      const quota = parseFloat(value);
      let tipoTerreno = '';
      
      if (!isNaN(quota)) {
        if (quota <= 300) {
          tipoTerreno = 'Pianura';
        } else if (quota <= 700) {
          tipoTerreno = 'Collina';
        } else {
          tipoTerreno = 'Montagna';
        }
      }
      
      setFormData(prev => ({ ...prev, [field]: value, tipoTerreno }));
    }
  };

  const handleGPS = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude.toFixed(7);
          const lon = position.coords.longitude.toFixed(7);
          setFormData(prev => ({
            ...prev,
            latitudine: lat,
            longitudine: lon
          }));
          // Aggiorna lo store: SITAC legge latitudine/longitudine da qui
          updateCOAU({ latitudine: lat, longitudine: lon } as any);
          alert(`✅ Coordinate acquisite!\nLat: ${lat}\nLon: ${lon}`);
        },
        (error) => {
          alert('❌ Errore acquisizione GPS: ' + error.message);
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert('❌ GPS non disponibile su questo dispositivo');
    }
  };

  const handleMappaClick = () => {
    if (onOpenMap) {
      onOpenMap();
    }
  };

  // Toggle ostacolo: TUTTI solo uno alla volta visibile, ma SALVANO i dati per il report
  const handleToggleOstacolo = (ostacolo: string) => {
    if (ostacoloVisibileCorrente === ostacolo) {
      // Click sullo stesso → CHIUDI pannello
      setOstacoloVisibileCorrente(null);
    } else {
      // Click su nuovo → CHIUDI precedente, APRI nuovo
      setOstacoloVisibileCorrente(ostacolo);
      
      // AGGIUNGI all'elenco compilati se non c'è già
      setFormData(prev => {
        const giàCompilato = prev.ostacoliCompilati.includes(ostacolo);
        
        if (giàCompilato) {
          return prev; // Già compilato, non fare nulla
        }
        
        // NUOVO ostacolo: inizializza i dati
        const nuoviCompilati = [...prev.ostacoliCompilati, ostacolo];
        
        // Se non è Visibilità, inizializza con "Direzione"
        if (ostacolo !== 'Visibilità') {
          return {
            ...prev,
            ostacoliCompilati: nuoviCompilati,
            ostacoliDirezioni: {
              ...prev.ostacoliDirezioni,
              [ostacolo]: 'Direzione'
            }
          };
        }
        
        return {
          ...prev,
          ostacoliCompilati: nuoviCompilati
        };
      });
    }
  };

  // Toggle ostacolo TERRA: stessa logica degli aerei
  const handleToggleOstacoloTerra = (ostacolo: string) => {
    if (ostacoloTerraVisibileCorrente === ostacolo) {
      // Click sullo stesso → CHIUDI pannello
      setOstacoloTerraVisibileCorrente(null);
    } else {
      // Click su nuovo → CHIUDI precedente, APRI nuovo
      setOstacoloTerraVisibileCorrente(ostacolo);
      
      // AGGIUNGI all'elenco compilati se non c'è già
      setFormData(prev => {
        const giàCompilato = prev.ostacoliTerraCompilati.includes(ostacolo);
        
        if (!giàCompilato) {
          return {
            ...prev,
            ostacoliTerraCompilati: [...prev.ostacoliTerraCompilati, ostacolo]
          };
        }
        
        return prev;
      });
    }
  };

  const getValoreColor = (valore: number | null): string => {
    if (!valore) return '#666';
    const val = valoriAmbientali.find(v => v.value === valore);
    return val ? val.color : '#666';
  };

  const generaTestoReport = (): string => {
    return generaReportCOAU(formData as any);
  };

  const scaricaAllegatoFallback = (testo: string, filename: string) => {
    const blob = new Blob([testo], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleInviaEmail = async () => {
    const reportText = generaTestoReport();
    const filename = `COAU_Report_${new Date().toISOString().slice(0, 10)}.txt`;

    // Su Android/Capacitor il blob download via link.click() può navigare via dall'app
    if (Capacitor.isNativePlatform()) {
      setNomeAllegatoEmail('');
      setShowModalEmail(true);
      return;
    }

    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await (window as any).showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'File di testo', accept: { 'text/plain': ['.txt'] } }],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(reportText);
        await writable.close();
        setNomeAllegatoEmail(filename);
        setShowModalEmail(true);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          scaricaAllegatoFallback(reportText, filename);
          setNomeAllegatoEmail(filename);
          setTimeout(() => setShowModalEmail(true), 300);
        }
      }
    } else {
      scaricaAllegatoFallback(reportText, filename);
      setNomeAllegatoEmail(filename);
      setTimeout(() => setShowModalEmail(true), 300);
    }
  };

  return (
    <div className="coau-container">
      {/* SEZIONE ORARIO */}
      <CollapsibleSection
        id="orario"
        title="Orario"
        emoji="🕐"
        isOpen={openSections.orario}
        onToggle={() => toggleSection('orario')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>ORA ACCERT. INCEN *</label>
            <MicTimeInput
              value={formData.oraAccertamento}
              onChange={(e) => handleInputChange('oraAccertamento', e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>ORA RICH. AL COAU *</label>
            <MicTimeInput
              value={formData.oraRichiesta}
              onChange={(e) => handleInputChange('oraRichiesta', e.target.value)}
              min={formData.oraAccertamento || undefined}
              required
              style={oraRichiestaError ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.3)' } : undefined}
            />
            {oraRichiestaError && (
              <small style={{ color: '#ef4444', fontSize: 11, marginTop: 3, display: 'block' }}>
                ⚠ Non può precedere l'ora di accertamento ({formData.oraAccertamento})
              </small>
            )}
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label>RICHIESTA PER *</label>
            <button
              className="btn-modal-trigger"
              onClick={() => setShowRichiestaModal(true)}
            >
              {formData.richiestaPer || 'Seleziona richiesta...'}
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* LOCALIZZAZIONE */}
      <CollapsibleSection
        id="localizzazione"
        title="Localizzazione"
        emoji="📍"
        isOpen={openSections.localizzazione}
        onToggle={() => toggleSection('localizzazione')}
      >
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>COMUNE *</label>
            <MicInput
              type="text"
              placeholder="Comune..."
              value={formData.comune}
              onChange={(e) => handleInputChange('comune', e.target.value)}
              required
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>PROVINCIA</label>
            <ProvinciaInput
              value={formData.provincia}
              onChange={(sigla) => handleInputChange('provincia', sigla)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 3 }}>
            <label>LOCALITÀ / INDIRIZZO</label>
            <MicInput
              type="text"
              placeholder="Via, contrada, località..."
              value={formData.indirizzo}
              onChange={(e) => handleInputChange('indirizzo', e.target.value)}
            />
          </div>
          <div className="geo-buttons">
            <button className="btn-gps-main" onClick={handleGPS}>
              📡 GPS
            </button>
            <button className="btn-map-main" onClick={handleMappaClick}>
              🗺️ MAPPA
            </button>
          </div>
        </div>
        {formData.latitudine && formData.longitudine && (
          <div className="coord-display" style={{ backgroundColor: '#000000' }}>
            📍 Lat: {formData.latitudine} | Lon: {formData.longitudine}
          </div>
        )}
      </CollapsibleSection>

      {/* VEGETAZIONE BRUCIATA */}
      <CollapsibleSection
        id="bruciato"
        title="Bruciato"
        emoji="🔥"
        isOpen={openSections.bruciato}
        onToggle={() => toggleSection('bruciato')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>VALORE AMBIENTALE</label>
            <button
              className="btn-valore-display"
              style={{ backgroundColor: getValoreColor(formData.valoreBruciato) }}
              onClick={() => setShowValoreBruciatoModal(true)}
            >
              {formData.valoreBruciato || 'Seleziona...'}
            </button>
          </div>
          <div className="form-group">
            <label>TIPOLOGIA</label>
            <button
              className="btn-tipologia-display"
              onClick={() => setShowTipologiaBruciatoModal(true)}
            >
              {formData.tipologiaBruciato || 'Seleziona...'}
            </button>
          </div>
          <div className="form-group">
            <label>ETTARI (ha)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.ettariBruciato}
              onChange={(e) => handleInputChange('ettariBruciato', e.target.value)}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* A RISCHIO */}
      <CollapsibleSection
        id="rischio"
        title="A Rischio"
        emoji="⚠️"
        isOpen={openSections.rischio}
        onToggle={() => toggleSection('rischio')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>VALORE AMBIENTALE</label>
            <button
              className="btn-valore-display"
              style={{ backgroundColor: getValoreColor(formData.valoreRischio) }}
              onClick={() => setShowValoreRischioModal(true)}
            >
              {formData.valoreRischio || 'Seleziona...'}
            </button>
          </div>
          <div className="form-group">
            <label>TIPOLOGIA</label>
            <button
              className="btn-tipologia-display"
              onClick={() => setShowTipologiaRischioModal(true)}
            >
              {formData.tipologiaRischio || 'Seleziona...'}
            </button>
          </div>
          <div className="form-group">
            <label>ETTARI (ha)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={formData.ettariRischio}
              onChange={(e) => handleInputChange('ettariRischio', e.target.value)}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* FRONTE FUOCO */}
      <CollapsibleSection
        id="fronteFuoco"
        title="Fronte del Fuoco"
        emoji="🔥"
        isOpen={openSections.fronteFuoco}
        onToggle={() => toggleSection('fronteFuoco')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>UNICO (mt)</label>
            <input
              type="number"
              min="0"
              value={formData.fronteUnico}
              onChange={(e) => handleInputChange('fronteUnico', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>DIVERSI N°</label>
            <input
              type="number"
              min="0"
              value={formData.fronteDiversiN}
              onChange={(e) => handleInputChange('fronteDiversiN', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>DIVERSI (mt)</label>
            <input
              type="number"
              min="0"
              value={formData.fronteDiversiMt}
              onChange={(e) => handleInputChange('fronteDiversiMt', e.target.value)}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* VENTO */}
      <CollapsibleSection
        id="vento"
        title="Vento"
        emoji="💨"
        isOpen={openSections.vento}
        onToggle={() => toggleSection('vento')}
      >
        <button
          className="btn-vento-trigger"
          onClick={() => setShowVentoModal(true)}
        >
          {formData.intensitaVento && formData.direzioneVento
            ? `${formData.intensitaVento}${formData.velocitaVentoKmh ? ` · ${formData.velocitaVentoKmh} km/h` : ''} — ${formData.direzioneVento.direzione} (${formData.direzioneVento.nome})`
            : 'Imposta vento...'}
        </button>
      </CollapsibleSection>

      {/* OROGRAFIA */}
      <CollapsibleSection
        id="orografia"
        title="Orografia"
        emoji="⛰️"
        isOpen={openSections.orografia}
        onToggle={() => toggleSection('orografia')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>QUOTA (m s.l.m.)</label>
            <input
              type="number"
              min="0"
              placeholder="Quota in metri"
              value={formData.quota}
              onChange={(e) => handleInputChange('quota', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>TIPO TERRENO</label>
            <div className="tipo-terreno-display">
              {formData.tipoTerreno === 'Pianura' && '🌾 Pianura'}
              {formData.tipoTerreno === 'Collina' && '🏞️ Collina'}
              {formData.tipoTerreno === 'Montagna' && '⛰️ Montagna'}
              {!formData.tipoTerreno && 'Inserisci quota...'}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* INFRASTRUTTURE E INSEDIAMENTI */}
      <CollapsibleSection
        id="criticita"
        title="Criticità"
        emoji="🏗️"
        isOpen={openSections.criticita}
        onToggle={() => toggleSection('criticita')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>INFRASTRUTTURE</label>
            <div className="radio-group">
              <button
                className={formData.hasInfrastrutture === 'SI' ? 'active' : ''}
                onClick={() => handleInputChange('hasInfrastrutture', 'SI')}
              >
                SI
              </button>
              <button
                className={formData.hasInfrastrutture === 'NO' ? 'active' : ''}
                onClick={() => handleInputChange('hasInfrastrutture', 'NO')}
              >
                NO
              </button>
            </div>
            {formData.hasInfrastrutture === 'SI' && (
              <MicInput
                type="text"
                className="note-mini"
                placeholder="Note infrastrutture..."
                value={formData.noteInfrastrutture}
                onChange={(e) => handleInputChange('noteInfrastrutture', e.target.value)}
              />
            )}
          </div>
          <div className="form-group">
            <label>INSEDIAMENTI ABITATIVI</label>
            <div className="radio-group">
              <button
                className={formData.hasInsediamenti === 'SI' ? 'active' : ''}
                onClick={() => handleInputChange('hasInsediamenti', 'SI')}
              >
                SI
              </button>
              <button
                className={formData.hasInsediamenti === 'NO' ? 'active' : ''}
                onClick={() => handleInputChange('hasInsediamenti', 'NO')}
              >
                NO
              </button>
            </div>
            {formData.hasInsediamenti === 'SI' && (
              <MicInput
                type="text"
                className="note-mini"
                placeholder="Note insediamenti..."
                value={formData.noteInsediamenti}
                onChange={(e) => handleInputChange('noteInsediamenti', e.target.value)}
              />
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* OSTACOLI AEREI */}
      <CollapsibleSection
        id="ostacoliAereiDett"
        title="Ostacoli Aerei"
        emoji="✈️"
        isOpen={openSections.ostacoliAereiDett}
        onToggle={() => toggleSection('ostacoliAereiDett')}
      >
        <div className="ostacoli-grid">
          {ostacoliOptions.map((ostacolo) => (
            <button
              key={ostacolo}
              className={`tag-btn ${formData.ostacoliCompilati.includes(ostacolo) ? 'active' : ''}`}
              onClick={() => handleToggleOstacolo(ostacolo)}
            >
              {ostacolo}
            </button>
          ))}
        </div>

        {/* ELETTRODOTTI - Mostra solo se è il visibile corrente */}
        {ostacoloVisibileCorrente === 'Elettrodotti' && (
          <div className="ostacolo-panel">
            <h4 className="ostacolo-title">⚡ Elettrodotti</h4>
            <div className="form-row">
              <div className="form-group">
                <label>STATO</label>
                <input
                  type="text"
                  value={formData.elettrodotto.stato}
                  placeholder="Seleziona stato..."
                  readOnly
                  onClick={() => setShowStatoElettrodottoModal(true)}
                  style={{ cursor: 'pointer', background: 'rgba(255, 255, 255, 0.1)' }}
                />
              </div>
              <div className="form-group">
                <label>TENSIONE</label>
                <button
                  className="btn-modal-trigger"
                  onClick={() => setShowTensioneModal(true)}
                >
                  {formData.elettrodotto.tensione || 'Seleziona tensione...'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <MicInput
                type="text"
                className="input-direzione"
                value={formData.elettrodotto.direzione}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    elettrodotto: { ...prev.elettrodotto, direzione: e.target.value }
                  }))
                }
              />
            </div>
          </div>
        )}

        {/* VISIBILITÀ - Mostra solo se è il visibile corrente */}
        {ostacoloVisibileCorrente === 'Visibilità' && (
          <div className="ostacolo-panel">
            <h4 className="ostacolo-title">👁️ Visibilità</h4>
            <MicTextarea
              rows={4}
              className="textarea-visibilita"
              placeholder="Inserisci note sulla visibilità..."
              value={formData.visibilitaNote}
              onChange={(e) => (e) => setFormData(prev => ({ ...prev, visibilitaNote: e.target.value }))}
            />
          </div>
        )}

        {/* ALTRI OSTACOLI - Mostra solo se è il visibile corrente */}
        {ostacoliOptions
          .filter(o => o !== 'Elettrodotti' && o !== 'Visibilità')
          .filter(o => ostacoloVisibileCorrente === o)
          .map((ostacolo) => (
            <div key={ostacolo} className="ostacolo-panel">
              <h4 className="ostacolo-title">{ostacolo}</h4>
              <MicInput
                type="text"
                className="input-direzione"
                value={formData.ostacoliDirezioni[ostacolo] || 'Direzione'}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    ostacoliDirezioni: {
                      ...prev.ostacoliDirezioni,
                      [ostacolo]: e.target.value
                    }
                  }))
                }
              />
            </div>
          ))}
      </CollapsibleSection>

      {/* OSTACOLI A TERRA */}
      <CollapsibleSection
        id="ostacoliTerraDett"
        title="Ostacoli a Terra"
        emoji="🏗️"
        isOpen={openSections.ostacoliTerraDett}
        onToggle={() => toggleSection('ostacoliTerraDett')}
      >
        <div className="ostacoli-grid">
          {ostacoliTerraOptions.map((ostacolo) => (
            <button
              key={ostacolo}
              className={`tag-btn ${formData.ostacoliTerraCompilati.includes(ostacolo) ? 'active' : ''}`}
              onClick={() => handleToggleOstacoloTerra(ostacolo)}
            >
              {ostacolo}
            </button>
          ))}
        </div>

        {/* TRALICCI ELETTRODOTTO - Copia da Elettrodotti aerei + "alto" */}
        {ostacoloTerraVisibileCorrente === 'Tralicci elettrodotto' && (
          <div className="ostacolo-panel">
            <h4 className="ostacolo-title">⚡ Tralicci elettrodotto</h4>
            <div className="form-row">
              <div className="form-group">
                <label>STATO</label>
                <select
                  value={formData.elettrodotto.stato}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      elettrodotto: { ...prev.elettrodotto, stato: e.target.value }
                    }))
                  }
                >
                  <option>Attivo</option>
                  <option>Disattivato</option>
                  <option>In Disattivazione</option>
                </select>
              </div>
              <div className="form-group">
                <label>TENSIONE</label>
                <button
                  className="btn-modal-trigger"
                  onClick={() => setShowTensioneModal(true)}
                >
                  {formData.elettrodotto.tensione || 'Seleziona tensione...'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <MicInput
                type="text"
                className="input-direzione"
                value={
                  formData.ostacoliTerraDirezioni['Tralicci elettrodotto'] || 
                  getTestoDaOstacoloAereo('Tralicci elettrodotto')
                }
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    ostacoliTerraDirezioni: {
                      ...prev.ostacoliTerraDirezioni,
                      'Tralicci elettrodotto': e.target.value
                    }
                  }))
                }
              />
            </div>
          </div>
        )}

        {/* ALTRI OSTACOLI A TERRA - Copia da ostacoli aerei corrispondenti + "alto" */}
        {ostacoliTerraOptions
          .filter(o => o !== 'Tralicci elettrodotto')
          .filter(o => ostacoloTerraVisibileCorrente === o)
          .map((ostacolo) => (
            <div key={ostacolo} className="ostacolo-panel">
              <h4 className="ostacolo-title">{ostacolo}</h4>
              <MicInput
                type="text"
                className="input-direzione"
                value={
                  formData.ostacoliTerraDirezioni[ostacolo] || 
                  getTestoDaOstacoloAereo(ostacolo)
                }
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    ostacoliTerraDirezioni: {
                      ...prev.ostacoliTerraDirezioni,
                      [ostacolo]: e.target.value
                    }
                  }))
                }
              />
            </div>
          ))}
      </CollapsibleSection>

      {/* FONTE IDRICA */}
      <CollapsibleSection
        id="fontiIdriche"
        title="Fonte Idrica"
        emoji="💧"
        isOpen={openSections.fontiIdriche}
        onToggle={() => toggleSection('fontiIdriche')}
      >
        {/* AEREI */}
        <div className="fonte-subsection">
          <h4 className="fonte-subtitle">✈️ Aerei</h4>
          <div className="form-row">
            <div className="form-group">
              <label>REGIONE</label>
              <button
                className="btn-modal-trigger"
                onClick={() => setShowRegioneAereiModal(true)}
              >
                {formData.fonteIdricaAerei.regione || 'Seleziona regione...'}
              </button>
            </div>
            <div className="form-group">
              <label>BACINO</label>
              <button
                className="btn-modal-trigger"
                onClick={() => {
                  if (!formData.fonteIdricaAerei.regione) {
                    alert('⚠️ Seleziona prima una regione!');
                    return;
                  }
                  setShowBacinoAereiModal(true);
                }}
                disabled={!formData.fonteIdricaAerei.regione}
              >
                {formData.fonteIdricaAerei.bacino || 'Seleziona bacino...'}
              </button>
            </div>
            {formData.fonteIdricaAerei.bacinoData && (
              <button
                className="btn-mappa-bacino"
                onClick={() => setShowMappaBacinoModal(true)}
              >
                🗺️ MAPPA
              </button>
            )}
          </div>
          {formData.fonteIdricaAerei.bacinoData && (
            <div className="bacino-info">
              📍 <strong>{formData.fonteIdricaAerei.bacinoData.name}</strong> - 
              Categoria: <span className={`cat-badge cat-${formData.fonteIdricaAerei.bacinoData.cat}`}>
                {formData.fonteIdricaAerei.bacinoData.cat}
              </span> - 
              Coord: {formData.fonteIdricaAerei.bacinoData.coords}
              {getDistanzaBacino() && (
                <span className="distanza-badge">📏 Distanza: {getDistanzaBacino()}</span>
              )}
            </div>
          )}
        </div>

        {/* ELICOTTERI */}
        <div className="fonte-subsection">
          <h4 className="fonte-subtitle">🚁 Elicotteri</h4>
          <div className="form-group">
            <label>TIPO / DETTAGLI</label>
            <MicInput
              type="text"
              placeholder="Es: Vasca, Fiume, Mare..."
              value={formData.fonteIdricaElicotteri.dettagli}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  fonteIdricaElicotteri: {
                    ...prev.fonteIdricaElicotteri,
                    dettagli: e.target.value
                  }
                }))
              }
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* PERSONALE E MEZZI */}
      <CollapsibleSection
        id="personale"
        title="Personale e Mezzi"
        emoji="👥"
        isOpen={openSections.personale}
        onToggle={() => toggleSection('personale')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>COORDINATORE</label>
            <div className="radio-group">
              <button
                className={formData.coordinatore === 'SI' ? 'active' : ''}
                onClick={() => handleInputChange('coordinatore', 'SI')}
              >
                SI
              </button>
              <button
                className={formData.coordinatore === 'NO' ? 'active' : ''}
                onClick={() => handleInputChange('coordinatore', 'NO')}
              >
                NO
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label>N° PERSONE</label>
            <input
              type="number"
              min="0"
              placeholder="0"
              value={formData.numeroPersone}
              onChange={(e) => handleInputChange('numeroPersone', e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>NOMINATIVO DOS (Codice Univoco)</label>
            <MicInput
              type="text"
              placeholder="Es: DOS-12345"
              value={formData.nominativoDOS}
              onChange={(e) => handleInputChange('nominativoDOS', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>RADIO FREQUENZA T.B.T.</label>
            <button
              className="btn-modal-trigger"
              onClick={() => setShowRadioFrequenzaModal(true)}
            >
              {formData.radioFrequenzaTBT || 'Seleziona frequenza...'}
            </button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>SQUADRE</label>
            <div className="radio-group">
              <button
                className={formData.squadre === 'SI' ? 'active' : ''}
                onClick={() => handleInputChange('squadre', 'SI')}
              >
                SI
              </button>
              <button
                className={formData.squadre === 'NO' ? 'active' : ''}
                onClick={() => handleInputChange('squadre', 'NO')}
              >
                NO
              </button>
            </div>
          </div>
          
          <div className="form-group">
            <label>AEROMOBILI REG.</label>
            <div className="radio-group">
              <button
                className={formData.aeromobiliReg === 'SI' ? 'active' : ''}
                onClick={() => handleInputChange('aeromobiliReg', 'SI')}
              >
                SI
              </button>
              <button
                className={formData.aeromobiliReg === 'NO' ? 'active' : ''}
                onClick={() => handleInputChange('aeromobiliReg', 'NO')}
              >
                NO
              </button>
            </div>
          </div>
        </div>

        {formData.aeromobiliReg === 'SI' && (
          <div className="aeromobile-panel">
            <div className="form-row">
              <div className="form-group">
                <label>NOME AEROMOBILE (Sigla)</label>
                <MicInput
                  type="text"
                  placeholder="Es: I-AIBQ"
                  value={formData.nomeAeromobile}
                  onChange={(e) => handleInputChange('nomeAeromobile', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>RADIO AEROMOBILE</label>
                <MicInput
                  type="text"
                  placeholder="Es: 122.150 MHz"
                  value={formData.radioAeromobile}
                  onChange={(e) => handleInputChange('radioAeromobile', e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* RITARDANTE */}
      <CollapsibleSection
        id="ritardante"
        title="Ritardante"
        emoji="🧯"
        isOpen={openSections.ritardante}
        onToggle={() => toggleSection('ritardante')}
      >
        <div className="form-row">
          <div className="form-group">
            <label>UTILIZZO RITARDANTE</label>
            <div className="ritardante-buttons">
              <button
                className={`btn-ritardante ${formData.ritardante === 'NO' ? 'active' : ''}`}
                onClick={() => handleInputChange('ritardante', 'NO')}
              >
                NO
              </button>
              <button
                className={`btn-ritardante ${formData.ritardante === 'SI - Tutte Sort.' ? 'active' : ''}`}
                onClick={() => handleInputChange('ritardante', 'SI - Tutte Sort.')}
              >
                SI - Tutte Sort.
              </button>
              <button
                className={`btn-ritardante ${formData.ritardante === 'SI - 1° Sortita' ? 'active' : ''}`}
                onClick={() => handleInputChange('ritardante', 'SI - 1° Sortita')}
              >
                SI - 1° Sortita
              </button>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>NOTE</label>
          <MicTextarea
            rows={3}
            placeholder="Note sul ritardante..."
            value={formData.noteRitardante}
            onChange={(e) => (e) => handleInputChange('noteRitardante', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      {/* FIRMA */}
      <CollapsibleSection
        id="firma"
        title="Firma"
        emoji="✍️"
        isOpen={openSections.firma}
        onToggle={() => toggleSection('firma')}
      >
        <div className="form-group">
          <label>FIRMA DEL RESPONSABILE</label>
          <MicInput
            type="text"
            placeholder="Nome e Cognome..."
            value={formData.firma}
            onChange={(e) => handleInputChange('firma', e.target.value)}
            className="input-firma"
          />
        </div>
      </CollapsibleSection>

      {/* BARRA AZIONI FINALE */}
      <div className="actions-bar">
        <button className="btn-action btn-email" onClick={handleInviaEmail}>
          📧 Invia Email
        </button>
      </div>

      {/* BUSSOLA FISSA */}
      <button className="btn-bussola-fixed" onClick={() => setShowBussolaModal(true)}>
        🧭
      </button>

      {/* MODALE INVIO EMAIL */}
      {showModalEmail && (
        <ModalInvioEmail
          onClose={() => setShowModalEmail(false)}
          comune={formData.comune}
          provincia={formData.provincia}
          nominativoDOS={formData.nominativoDOS}
          dataOra={new Date().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          riepilogoScheda={
      Capacitor.isNativePlatform()
        ? generaTestoReport()
        : `Comune: ${formData.comune || 'N.D.'}\nProvincia: ${formData.provincia || 'N.D.'}\nLocalità: ${formData.indirizzo || 'N.D.'}\nCoordinate: ${formData.latitudine || ''}, ${formData.longitudine || ''}\nOra accertamento: ${formData.oraAccertamento || 'N.D.'}`
    }
          nomeAllegato={nomeAllegatoEmail}
        />
      )}

      {/* MODALI - continua con tutti i modali come prima... */}

      {/* Modale Richiesta Per */}
      {showRichiestaModal && (
        <div className="modal-overlay" onClick={() => setShowRichiestaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Richiesta Per</h3>
              <button className="btn-close" onClick={() => setShowRichiestaModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {richiestePerOptions.map((opt) => (
                <button
                  key={opt.value}
                  className="modal-option-btn"
                  onClick={() => {
                    handleInputChange('richiestaPer', opt.label);
                    setShowRichiestaModal(false);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Valore Bruciato */}
      {showValoreBruciatoModal && (
        <div className="modal-overlay" onClick={() => setShowValoreBruciatoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Valore Ambientale - Bruciato</h3>
              <button className="btn-close" onClick={() => setShowValoreBruciatoModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {valoriAmbientali.map((val) => (
                <button
                  key={val.value}
                  className="modal-valore-btn"
                  style={{ borderLeftColor: val.color }}
                  onClick={() => {
                    handleInputChange('valoreBruciato', val.value);
                    setShowValoreBruciatoModal(false);
                  }}
                >
                  <span className="valore-numero" style={{ color: val.color }}>
                    {val.value}
                  </span>
                  <span className="valore-desc">{val.label.split(' - ')[1]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Tipologia Bruciato */}
      {showTipologiaBruciatoModal && (
        <div className="modal-overlay" onClick={() => setShowTipologiaBruciatoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tipologia - Bruciato</h3>
              <button className="btn-close" onClick={() => setShowTipologiaBruciatoModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {tipologie.map((tip) => (
                <button
                  key={tip.value}
                  className="modal-tipologia-btn"
                  onClick={() => {
                    handleInputChange('tipologiaBruciato', tip.value);
                    setShowTipologiaBruciatoModal(false);
                  }}
                >
                  <span className="tipologia-numero">{tip.value}</span>
                  <span className="tipologia-desc">{tip.label.split(' - ')[1]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Valore Rischio */}
      {showValoreRischioModal && (
        <div className="modal-overlay" onClick={() => setShowValoreRischioModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Valore Ambientale - A Rischio</h3>
              <button className="btn-close" onClick={() => setShowValoreRischioModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {valoriAmbientali.map((val) => (
                <button
                  key={val.value}
                  className="modal-valore-btn"
                  style={{ borderLeftColor: val.color }}
                  onClick={() => {
                    handleInputChange('valoreRischio', val.value);
                    setShowValoreRischioModal(false);
                  }}
                >
                  <span className="valore-numero" style={{ color: val.color }}>
                    {val.value}
                  </span>
                  <span className="valore-desc">{val.label.split(' - ')[1]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Tipologia Rischio */}
      {showTipologiaRischioModal && (
        <div className="modal-overlay" onClick={() => setShowTipologiaRischioModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Tipologia - A Rischio</h3>
              <button className="btn-close" onClick={() => setShowTipologiaRischioModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {tipologie.map((tip) => (
                <button
                  key={tip.value}
                  className="modal-tipologia-btn"
                  onClick={() => {
                    handleInputChange('tipologiaRischio', tip.value);
                    setShowTipologiaRischioModal(false);
                  }}
                >
                  <span className="tipologia-numero">{tip.value}</span>
                  <span className="tipologia-desc">{tip.label.split(' - ')[1]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Vento */}
      {showVentoModal && (
        <div className="modal-overlay" onClick={() => setShowVentoModal(false)}>
          <div className="modal-content modal-vento" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💨 Configurazione Vento</h3>
              <button className="btn-close" onClick={() => setShowVentoModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <button
                className="btn-importa-sitac"
                onClick={importaVentoDaSITAC}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  marginBottom: '16px',
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
                  border: '1px solid #38bdf8',
                  borderRadius: '8px',
                  color: '#38bdf8',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                📡 Importa dati vento da SITAC
              </button>
              <label className="modal-label">INTENSITÀ</label>
              <div className="intensita-buttons">
                {['Debole', 'Moderato', 'Forte'].map((int) => (
                  <button
                    key={int}
                    className={`btn-intensita ${formData.intensitaVento === int ? 'active' : ''}`}
                    onClick={() => handleInputChange('intensitaVento', int)}
                  >
                    {int}
                  </button>
                ))}
              </div>

              <label className="modal-label" style={{ marginTop: '12px' }}>VELOCITÀ (km/h)</label>
              <input
                type="number"
                min={0}
                max={200}
                placeholder="es. 18"
                value={formData.velocitaVentoKmh ?? ''}
                onChange={(e) => {
                  const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                  setFormData(prev => ({ ...prev, velocitaVentoKmh: v }));
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #334155',
                  background: '#1e293b',
                  color: '#e2e8f0',
                  fontSize: '1rem',
                  marginBottom: '4px',
                }}
              />

              <label className="modal-label" style={{ marginTop: '12px' }}>DIREZIONE (Rosa dei Venti)</label>
              <div className="rosa-venti-grid">
                {rosaVenti.map((rosa) => (
                  <button
                    key={rosa.direzione}
                    className={`rosa-btn ${formData.direzioneVento?.direzione === rosa.direzione ? 'active' : ''}`}
                    onClick={() => {
                      handleInputChange('direzioneVento', rosa);
                    }}
                  >
                    <div className="rosa-gradi">{rosa.gradi}</div>
                    <div className="rosa-dir">{rosa.direzione}</div>
                    <div className="rosa-nome">{rosa.nome}</div>
                    <div className="rosa-prov">📍 {rosa.provenienza}</div>
                    <div className="rosa-char">{rosa.caratteristiche}</div>
                  </button>
                ))}
              </div>

              <button
                className="btn-modal-confirm"
                onClick={() => setShowVentoModal(false)}
              >
                ✅ Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale GPS/Mappa */}
      {showGPSModal && (
        <div className="modal-overlay" onClick={() => setShowGPSModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📍 Inserimento Coordinate</h3>
              <button className="btn-close" onClick={() => setShowGPSModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>LATITUDINE</label>
                <input
                  type="text"
                  placeholder="es. 44.6404596"
                  value={formData.latitudine}
                  onChange={(e) => handleInputChange('latitudine', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>LONGITUDINE</label>
                <input
                  type="text"
                  placeholder="es. 10.9387246"
                  value={formData.longitudine}
                  onChange={(e) => handleInputChange('longitudine', e.target.value)}
                />
              </div>
              <button
                className="btn-modal-confirm"
                onClick={() => setShowGPSModal(false)}
              >
                ✅ Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Tensione Elettrodotto */}
      {showTensioneModal && (
        <div className="modal-overlay" onClick={() => setShowTensioneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚡ Tensione Elettrodotto</h3>
              <button className="btn-close" onClick={() => setShowTensioneModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {tensioniOptions.map((tensione) => (
                <button
                  key={tensione.value}
                  className="modal-tensione-btn"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      elettrodotto: { ...prev.elettrodotto, tensione: tensione.value }
                    }));
                    setShowTensioneModal(false);
                  }}
                >
                  <div className="tensione-value">{tensione.value}</div>
                  <div className="tensione-desc">{tensione.descrizione}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Stato Elettrodotto */}
      {showStatoElettrodottoModal && (
        <div className="modal-overlay" onClick={() => setShowStatoElettrodottoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚡ Stato Elettrodotto</h3>
              <button className="btn-close" onClick={() => setShowStatoElettrodottoModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {['Attivo', 'Disattivato', 'In Disattivazione'].map((stato) => (
                <button
                  key={stato}
                  className="option-btn"
                  style={{ marginBottom: '12px', width: '100%' }}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      elettrodotto: { ...prev.elettrodotto, stato }
                    }));
                    setShowStatoElettrodottoModal(false);
                  }}
                >
                  {stato}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Regione Aerei */}
      {showRegioneAereiModal && (
        <div className="modal-overlay" onClick={() => setShowRegioneAereiModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗺️ Seleziona Regione</h3>
              <button className="btn-close" onClick={() => setShowRegioneAereiModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="regioni-grid">
                {regioniItalia.map((regione) => (
                  <button
                    key={regione}
                    className="modal-regione-btn"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        fonteIdricaAerei: {
                          regione: regione,
                          bacino: '',
                          bacinoData: null
                        }
                      }));
                      setShowRegioneAereiModal(false);
                    }}
                  >
                    {regione}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale Bacino Aerei */}
      {showBacinoAereiModal && formData.fonteIdricaAerei.regione && (
        <div className="modal-overlay" onClick={() => setShowBacinoAereiModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💧 Bacini - {formData.fonteIdricaAerei.regione}</h3>
              <button className="btn-close" onClick={() => setShowBacinoAereiModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {baciniAereiJSON[formData.fonteIdricaAerei.regione].map((bacino) => (
                <button
                  key={bacino.name}
                  className="modal-bacino-btn"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      fonteIdricaAerei: {
                        ...prev.fonteIdricaAerei,
                        bacino: bacino.name,
                        bacinoData: bacino
                      }
                    }));
                    setShowBacinoAereiModal(false);
                  }}
                >
                  <div className="bacino-name">{bacino.name}</div>
                  <div className="bacino-details">
                    <span className={`cat-badge cat-${bacino.cat}`}>Cat. {bacino.cat}</span>
                    <span className="bacino-coords">📍 {bacino.coords}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale MAPPA Bacino */}
      {showMappaBacinoModal && formData.fonteIdricaAerei.bacinoData && (
        <div className="modal-overlay" onClick={() => setShowMappaBacinoModal(false)}>
          <div className="modal-content modal-mappa" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗺️ {formData.fonteIdricaAerei.bacinoData.name}</h3>
              <button className="btn-close" onClick={() => setShowMappaBacinoModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="mappa-info">
                <div className="mappa-info-row">
                  <strong>Bacino:</strong> {formData.fonteIdricaAerei.bacinoData.name}
                </div>
                <div className="mappa-info-row">
                  <strong>Categoria:</strong> 
                  <span className={`cat-badge cat-${formData.fonteIdricaAerei.bacinoData.cat}`}>
                    {formData.fonteIdricaAerei.bacinoData.cat}
                  </span>
                </div>
                <div className="mappa-info-row">
                  <strong>Coordinate:</strong> {formData.fonteIdricaAerei.bacinoData.coords}
                </div>
                {getDistanzaBacino() && (
                  <div className="mappa-info-row distanza-highlight">
                    <strong>📏 Distanza dall'incendio:</strong> 
                    <span className="distanza-value">{getDistanzaBacino()}</span>
                  </div>
                )}
              </div>
              <div className="mappa-container">
                <iframe
                  width="100%"
                  height="450"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight={0}
                  marginWidth={0}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                    parseFloat(formData.fonteIdricaAerei.bacinoData.coords.split(',')[1]) - 0.02
                  }%2C${
                    parseFloat(formData.fonteIdricaAerei.bacinoData.coords.split(',')[0]) - 0.02
                  }%2C${
                    parseFloat(formData.fonteIdricaAerei.bacinoData.coords.split(',')[1]) + 0.02
                  }%2C${
                    parseFloat(formData.fonteIdricaAerei.bacinoData.coords.split(',')[0]) + 0.02
                  }&layer=mapnik&marker=${formData.fonteIdricaAerei.bacinoData.coords}`}
                  style={{ border: '2px solid #ff6b35', borderRadius: '8px' }}
                />
                <div className="mappa-links">
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${formData.fonteIdricaAerei.bacinoData.coords.split(',')[0]}&mlon=${formData.fonteIdricaAerei.bacinoData.coords.split(',')[1]}#map=15/${formData.fonteIdricaAerei.bacinoData.coords}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-open-osm"
                  >
                    🗺️ Apri in OpenStreetMap
                  </a>
                  <a
                    href={`https://www.google.com/maps?q=${formData.fonteIdricaAerei.bacinoData.coords}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-open-gmaps"
                  >
                    📍 Apri in Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale Radio Frequenza TBT */}
      {showRadioFrequenzaModal && (
        <div className="modal-overlay" onClick={() => setShowRadioFrequenzaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📻 Radio Frequenza T.B.T.</h3>
              <button className="btn-close" onClick={() => setShowRadioFrequenzaModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {frequenzeRadioTBT.map((item) => (
                <button
                  key={item.freq}
                  className="modal-frequenza-btn"
                  onClick={() => {
                    handleInputChange('radioFrequenzaTBT', item.freq);
                    setShowRadioFrequenzaModal(false);
                  }}
                >
                  <div className="freq-value">{item.freq}</div>
                  <div className="freq-zona">{item.zona}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modale Bussola */}
      {showBussolaModal && (
        <div className="modal-overlay" onClick={() => setShowBussolaModal(false)}>
          <div className="modal-content modal-bussola" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🧭 Bussola</h3>
              <button className="btn-close" onClick={() => setShowBussolaModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="bussola-container">
                <div className="bussola-circle">
                  <div className="bussola-north">N</div>
                  <div className="bussola-east">E</div>
                  <div className="bussola-south">S</div>
                  <div className="bussola-west">O</div>
                  <div className="bussola-ne">NE</div>
                  <div className="bussola-se">SE</div>
                  <div className="bussola-so">SO</div>
                  <div className="bussola-no">NO</div>
                  <div className="bussola-needle"></div>
                </div>
              </div>
              <div className="bussola-legend">
                <div><strong>N</strong> - Nord (0°)</div>
                <div><strong>E</strong> - Est (90°)</div>
                <div><strong>S</strong> - Sud (180°)</div>
                <div><strong>O</strong> - Ovest (270°)</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
